import React, { useState } from "react";
import { Box, Container, TextField, Typography, Link } from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";
import { useNavigate } from "react-router-dom";
import BASE_URL from "../../../config/apiConfig";

// Email validation regex
const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const SignUpView = () => {
  const navigate = useNavigate();
  const [userName, setName] = useState("");
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [signupError, setSignupError] = useState("");

  // Validate the form fields
  const validateForm = () => {
    const newErrors = {};
    if (!userName.trim()) {
      newErrors.userName = "Name is required";
    } else if (userName.length > 20) {
      newErrors.userName = "Name must be 20 characters or less";
    }

    if (!email.trim()) {
      newErrors.email = "Email is required";
    } else if (!emailRegex.test(email)) {
      newErrors.email = "Invalid email address";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission

  const handleSignUp = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setLoading(true);
    setSignupError("");

    try {
      const response = await fetch(`${BASE_URL}/api/user/auth/signup`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ userName, email }),
      });

      if (response.ok) {
        const data = await response.json();

        // Save the email in localStorage
        localStorage.setItem("email", email);

        console.log("Signup successful:", data);

        // Navigate to GetOTP page
        navigate("/get-otp");
      } else {
        const errorData = await response.json();
        setSignupError(errorData.message || "Signup failed");
      }
    } catch (error) {
      console.error("Error during signup:", error);
      setSignupError("Error during signup. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const renderForm = (
    <Box
      component="form"
      onSubmit={handleSignUp}
      display="flex"
      flexDirection="column"
      alignItems="stretch"
      sx={{ width: "100%", maxWidth: "400px", mb: 2 }}
    >
      <TextField
        fullWidth
        name="name"
        label="Your Name"
        value={userName}
        onChange={(e) => setName(e.target.value)}
        error={!!errors.userName}
        helperText={errors.userName}
        sx={{
          mb: 3,
          "& label.Mui-focused": { color: "#51833a" },
          "& .MuiOutlinedInput-root": {
            "&:hover fieldset": { borderColor: "#51833a" },
            "&.Mui-focused fieldset": { borderColor: "#51833a" },
          },
        }}
        variant="outlined"
      />

      <TextField
        fullWidth
        name="email"
        label="Email Address"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        error={!!errors.email}
        helperText={errors.email}
        sx={{
          mb: 2,
          "& label.Mui-focused": { color: "#51833a" },
          "& .MuiOutlinedInput-root": {
            "&:hover fieldset": { borderColor: "#51833a" },
            "&.Mui-focused fieldset": { borderColor: "#51833a" },
          },
        }}
        variant="outlined"
      />

      {signupError && (
        <Typography variant="body2" color="error" sx={{ mb: 3 }}>
          {signupError}
        </Typography>
      )}

      <LoadingButton
        fullWidth
        size="large"
        type="submit"
        color="inherit"
        variant="contained"
        loading={loading}
        sx={{
          background: "linear-gradient(45deg, #51833a 30%, #66bb6a 90%)",
          color: "#fff",
          "&:hover": { backgroundColor: "#406027" },
          mb: 2,
          textTransform: "none",
          minHeight: "50px",
          borderRadius: "8px",
          transition: "0.3s ease",
          boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.1)", // Subtle shadow effect
        }}
      >
        Get OTP
      </LoadingButton>

      <Typography variant="body2" align="center">
        Already have an account?{" "}
        <Link
          href="/user/login"
          variant="body2"
          sx={{ color: "#51833a", "&:hover": { textDecoration: "underline" } }}
        >
          Sign In
        </Link>
      </Typography>
    </Box>
  );

  return (
    <Container
      sx={{
        height: "100vh",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        padding: { xs: 2, md: 0 },
        background: "linear-gradient(135deg, #f3f4f6 0%, #e5e7eb 100%)", // Gradient background
      }}
    >
      <Box
        sx={{
          backgroundColor: "#fff",
          minHeight: "70vh",
          borderRadius: "16px",
          boxShadow: "0px 10px 20px rgba(0, 0, 0, 0.1)",
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          width: "100%",
          maxWidth: "900px",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            padding: { xs: 3, md: 5 },
            flex: 1,
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "flex-start",
            backgroundColor: "#f9fafb",
          }}
        >
          <Box
            gap={1.5}
            display="flex"
            flexDirection="column"
            alignItems="flex-start"
            sx={{ mb: 5 }}
          >
            <Typography
              variant="h5"
              sx={{
                fontWeight: "bold",
                color: "#333",
                letterSpacing: "1px",
                textTransform: "uppercase",
              }}
            >
              Sign Up
            </Typography>
          </Box>

          {renderForm}
        </Box>

        {/* Right Side Illustration */}
        <Box
          sx={{
            backgroundColor: "#e5e7eb",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flex: 1,
            padding: { xs: 2, md: 4 },
            position: "relative",
          }}
        >
          <Box
            component="img"
            src="/Images/Login_Body.webp"
            alt="Task illustration"
            sx={{ width: "100%", maxWidth: "600px", borderRadius: "12px" }}
          />
          {/* Decorative Shape */}
          <Box
            sx={{
              width: "120px",
              height: "120px",
              backgroundColor: "#51833a",
              borderRadius: "50%",
              position: "absolute",
              bottom: "-20px",
              right: "-20px",
              opacity: 0.7,
              zIndex: -1,
            }}
          />
        </Box>
      </Box>
    </Container>
  );
};

export default SignUpView;
